import { useEffect, useState, useCallback } from 'react';

export function usePagination(
  fetchFn,
  { initialPage = 1, deps = [], responseDataKey, handleResponse } = {}
) {
  const [items, setItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(
    async (page) => {
      setLoading(true);
      try {
        const response = await fetchFn(page);

        const newData = handleResponse
          ? handleResponse(response)
          : responseDataKey
          ? response.data[responseDataKey].data
          : response.data;

        // Extract pagination info from response
        // Support different response structures
        let lastPage, total, perPage;

        if (responseDataKey) {
          total = response.data[responseDataKey]?.total || 0;
          perPage =
            response.data[responseDataKey]?.per_page ||
            response.data[responseDataKey]?.items_per_page ||
            10;
          lastPage =
            response.data[responseDataKey]?.last_page ||
            response.data[responseDataKey]?.total_pages ||
            Math.ceil(total / perPage) ||
            1;
        } else {
          total = response.total || 0;
          perPage = response.per_page || response.items_per_page || 10;
          lastPage =
            response.last_page ||
            response.total_pages ||
            Math.ceil(total / perPage) ||
            1;
        }

        setItems(newData || []);
        setTotalPages(lastPage);
        setTotalItems(total);
        setItemsPerPage(perPage);
        setCurrentPage(page);
      } catch (error) {
        console.error('Fetch error:', error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [fetchFn, handleResponse, responseDataKey]
  );

  // Reset and fetch when dependencies change
  useEffect(() => {
    setCurrentPage(initialPage);
    fetchData(initialPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPage, ...deps]);

  // Handle page change
  const handlePageChange = useCallback(
    (page) => {
      if (page >= 1 && page <= totalPages && page !== currentPage) {
        fetchData(page);
      }
    },
    [fetchData, totalPages, currentPage]
  );

  // Refetch function to manually reload data
  const refetch = useCallback(
    async (options = {}) => {
      const { preservePage = false, targetPage } = options;
      const pageToRefetch = preservePage
        ? targetPage || currentPage
        : initialPage;
      await fetchData(pageToRefetch);
    },
    [fetchData, currentPage, initialPage]
  );

  return {
    items,
    loading,
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    onPageChange: handlePageChange,
    refetch,
  };
}
